/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { getTelemetryObject, IModuleProps, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import { ISzfCarouselData } from './szf-carousel.data';
import { ISzfCarouselProps } from './szf-carousel.props.autogenerated';
import { ICarouselState } from './szf-carousel.state';

export interface ICarouselViewProps extends ISzfCarouselProps<{}>, ICarouselState {
    closeAnimation: string;
    carouselContainer: IModuleProps;
    telemetryContent: ITelemetryContent;
    next(): void;
    previous(): void;
    onIndicatorsClickHandler(): void;
    handleDismissCarousel(): void;
    handleOnExited(): void;
    handleOnExiting(): void;
}
export interface contentBlockSlot {
    props:{
        config:{
            className?:string
        }
    }
}
/**
 *
 * CarouselModule component.
 * @extends {React.PureComponent<ISzfCarouselProps<ISzfCarouselData>>}
 */
class CarouselModule extends React.PureComponent<ISzfCarouselProps<ISzfCarouselData>, ICarouselState> {
    private animating: boolean;

    private readonly telemetryContent: ITelemetryContent;

    constructor(props: ISzfCarouselProps<ISzfCarouselData>, state: ICarouselState) {
        super(props);
        this.state = { activeIndex: 0, closeNow: false, dismissCarousel: false };
        this.animating = false;
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
    }

    public componentDidMount(): void {
        if (this.props.context && this.props.context.request && this.props.context.request.cookies) {
            if (this.props.context.request.cookies.get(`CarouselDismiss_${this.props.id}`) === undefined) {
                this.props.context.request.cookies.set(`CarouselDismiss_${this.props.id}`, 'false');
                this.setState({ dismissCarousel: false });
            }
        }
    }

    public render(): JSX.Element | null {
        if (this.state.dismissCarousel) {
            return null;
        }

        const { className } = this.props.config;

        const viewProps = {
            // Context for telemetry and raw data for C1 to do customization
            // Including computed data, state data and methods
            ...this.props,
            ...this.state,
            telemetryContent: this.telemetryContent,
            carouselContainer: {
                moduleProps: this.props,
                className: classnames('ms-carousel', className)
            },
            closeAnimation: this.state.closeNow ? 'close-now' : '',
            next: this._next,
            previous: this._previous,
            onIndicatorsClickHandler: this._goToIndex,
            handleDismissCarousel: this._handleDismissCarousel,
            handleOnExited: this._onExited,
            handleOnExiting: this._onExiting
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    /**
     * Function implementation to dismiss the Carousel.
     */
    private readonly _handleDismissCarousel = (): void => {
        if (this.props.context && this.props.context.request && this.props.context.request.cookies) {
            this.props.context.request.cookies.set(`CarouselDismiss_${this.props.id}`, 'true');
            this.setState({ dismissCarousel: true });
        }
    };

    /**
     * Function implementation to move to next slide.
     */
    private readonly _next = (): void => {
        const { slots } = this.props;
        const nextIndex = this.state.activeIndex === (slots && slots.content && slots.content.length - 1) ? 0 : this.state.activeIndex + 1;
        // const Classes = slots && slots.content && (slots.content.length >0) && slots.content.map((contentBlockslot : React.ReactNode )=>{
        //     return contentBlockslot && contentBlockslot.props && contentBlockslot.props.config.className
        // })
        /* VSI | Customization | Start */
        const { parallaxEffect, imageClassesForParallaxEffect } = this.props.config;
        if(parallaxEffect && imageClassesForParallaxEffect?.length){
            const domBodyElement = document.getElementsByClassName('szf_homepage');
            imageClassesForParallaxEffect.forEach((Parallaxclass)=>{
                domBodyElement[0].classList.remove(Parallaxclass);
            });
            domBodyElement[0].classList.add(imageClassesForParallaxEffect[nextIndex]);
        }
        /* VSI | Customization | End */
        if (this.animating) {
            this.setState({ closeNow: true, activeIndex: nextIndex });
        } else {
            this.setState({ activeIndex: nextIndex });
        }
    };

    /**
     * Function implementation to move to previous slide.
     */
    private readonly _previous = (): void => {
        const slots = this.props.slots && this.props.slots.content;
        const nextIndex = this.state.activeIndex === 0 ? (slots ? slots.length - 1 : 0) : this.state.activeIndex - 1;
        /* VSI | Customization | Start */
        const { parallaxEffect, imageClassesForParallaxEffect } = this.props.config;
        if(parallaxEffect && imageClassesForParallaxEffect?.length){
            const domBodyElement = document.getElementsByClassName('szf_homepage');
            imageClassesForParallaxEffect.forEach((Parallaxclass)=>{
                domBodyElement[0].classList.remove(Parallaxclass);
            });
            domBodyElement[0].classList.add(imageClassesForParallaxEffect[nextIndex]);
        }
        /* VSI | Customization | End */
        if (this.animating) {
            this.setState({ closeNow: true, activeIndex: nextIndex });
        } else {
            this.setState({ activeIndex: nextIndex });
        }
    };

    private readonly _onExiting = (): void => {
        this.animating = true;
    };

    private readonly _onExited = (): void => {
        this.animating = false;
        this.setState({ closeNow: false });
    };

    /**
     * Function implementation to move to a given slide.
     * @param newIndex
     */
    private readonly _goToIndex = (newIndex: number): void => {
        if (this.animating) {
            return;
        }
        this.setState({ activeIndex: newIndex });
    };
}

export default CarouselModule;
